/**
 * HubEngineeringPage — HUB-REDESIGN P4.
 *
 * Single dense page that subsumes the legacy /queue, /shipped, /errors,
 * /logs, /events, /health surfaces. Reshelled from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/engineering.jsx,
 * wired to the existing endpoints (backend frozen for the redesign):
 *
 *   /hub/stats             — KPI strip (today's ships, errors, P0/P1
 *                            queue depth, watchdog count, migration,
 *                            uptime), last-ship sha + age, bundle hash.
 *   /hub/queue?status=*    — Queue panel (P0/P1/P2/P3 backlog) + Ships
 *                            panel (status=done, shipped_at_desc).
 *   /hub/errors            — Errors panel (24h hourly bars + recent
 *                            error rows with ack/resolve actions).
 *   /hub/logs/search       — Logs panel (live tail, level + since
 *                            filters).
 *   /hub/webhooks          — Events panel (audit stream of recent
 *                            inbound deliveries).
 *   /hub/health/alerts     — Watchdog panel (active count + per-rule
 *                            ack/resolve).
 *   /hub/health/rules      — Watchdog panel (all-rules grid).
 *
 * Per-panel auto-refresh varies — logs at 5s (live tail feel), every-
 * thing else at 30s (matches the rest of the hub). Each panel is a
 * collapsible <Card> whose head shows the endpoint tag, mirroring the
 * Overview bento + Tenants pages.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { formatTime, formatDateWithOptions } from '../utils/dateUtils'
import {
  Card, Endpoint, KPI, Pill, StatusDot, AreaChart, Bars, Icon,
  DepartmentObservability,
  type IconName,
} from './components'
import { PipelineBoard } from './PipelineBoard'
import { posthogCapture } from './analytics'
import {
  hubStats, hubErrors, hubLogs, hubWebhooks, hubHealth, hubAgentRuns,
  hubTriggers, hubSpawnRequest, hubCassStatus, hubBackups, hubGithubIssues,
  hubSiwaTrace, hubMissionPipeline,
  type HubStats, type HubErrorEvent,
  type HubLogEntry, type HubWebhookEvent, type HubHealthAlert,
  type HubHealthRule, type HubQueueActivityRow, type HubAgentRun,
  type TriggerStatus, type SpawnRequest, type WorkflowRunSummary,
  type CassStatus, type HubBackup, type GithubIssueItem,
  type SiwaEvent, type HubMissionPipelineResponse,
} from './api'

const GITHUB_REPO_PR_BASE = 'https://github.com/meadowlarkjoe/Tenon/pull'

/** codingCount — the authoritative "Coding" stage count from the shared
 *  /hub/mission/pipeline endpoint (HUB-ENG-NORMALIZE #1358). Engineering must
 *  read this, not the raw stats.active_claims (status:in-progress) count, which
 *  includes review-waiting items and so disagreed with Mission Control's
 *  "Coding" tile. Returns null while the pipeline is loading/unavailable. */
function codingCount(pipeline: HubMissionPipelineResponse | null): number | null {
  const s = pipeline?.stages.find(st => st.key === 'coding')
  return s ? s.count : null
}

/** Header pill for the Engineering pipeline strip — mirrors Mission Control's
 *  "{n} stuck" indicator so the two pages read identically. */
function pipelineStuckPill(pipeline: HubMissionPipelineResponse | null): React.ReactNode {
  if (!pipeline) return undefined
  const stuck = pipeline.stages.filter(s => s.stuck).length
  return stuck > 0 ? <Pill variant="err">{stuck} stuck</Pill> : undefined
}

/** #1380 — the queue table's status chip for status:in-progress issues used to
 *  just echo the raw label, recreating the coding-vs-review ambiguity the
 *  pipeline tiles already fixed (#1358/#1342): the tile said CODING: 0 while
 *  the table showed 3 IN-PROGRESS (all 3 had open review PRs — both readings
 *  were "true", just imprecise). Derives the same split the pipeline endpoint
 *  computes (mission_pipeline_handler.go's coding-stage exclusion), using its
 *  issue_review_prs linkage map — never claims CODING when that linkage is
 *  known-incomplete (review_overlap_ok false). */
function inProgressStage(
  issueNumber: number,
  pipeline: HubMissionPipelineResponse | null
): { label: string; prNumber?: number; unknown: boolean } {
  if (!pipeline) return { label: 'in-progress', unknown: true }
  const prNumber = pipeline.issue_review_prs?.[String(issueNumber)]
  if (prNumber) return { label: `IN REVIEW (PR #${prNumber})`, prNumber, unknown: false }
  if (pipeline.review_overlap_ok) return { label: 'CODING', unknown: false }
  return { label: 'in-progress', unknown: true }
}

// HUB-REPOINT (#1217): the workflow_items-era server rules (stuck_claim,
// stuck_preparing, stale_preparing, queued_stale_wip, session_age,
// manager_pulse_silent, coder_respawn_broken, in_progress_post_deploy_failed)
// were retired; the server now evaluates the GitHub-pipeline rules below. Kept
// in sync with WatchdogRules in internal/api/queue_watchdog_worker.go so the
// panel never renders phantom rows for rules that no longer exist.
const RULE_ORDER = [
  'idle_queue', 'coordinator_silent', 'merge_stall', 'deploy_stall',
  'orphaned_tree', 'stale_stashes', 'auth_lapsed', 'context_warning',
  'watcher_events', 'working_tree',
]

export default function HubEngineeringPage() {
  const [stats, setStats] = useState<HubStats | null>(null)
  const [pipeline, setPipeline] = useState<HubMissionPipelineResponse | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      // HUB-ENG-NORMALIZE (#1358): the pipeline strip + the KPI "Coding" reading
      // both come from the SAME /hub/mission/pipeline endpoint as Mission
      // Control, so a number shown on both pages can never disagree.
      const [s, pl] = await Promise.all([
        hubStats().catch(() => null),
        hubMissionPipeline().catch(() => null),
      ])
      if (cancelled) return
      setStats(s)
      setPipeline(pl)
      setRefreshedAt(new Date())
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <KPIStrip stats={stats} pipeline={pipeline} />
      <Card
        icon="git-branch" title="Pipeline"
        endpoint="/hub/mission/pipeline"
        right={pipelineStuckPill(pipeline)}
        bodyPad={false}
      >
        <PipelineBoard data={pipeline} />
      </Card>
      <div className="hub-collapse" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
        <GitHubIssuesPanel pipeline={pipeline} />
        <ShipsPanel stats={stats} />
      </div>
      <SiwaEventsPanel />
      <TriggersPanel />
      <ErrorsPanel />
      <SiwaEventsPanel />
      <div className="hub-collapse" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10 }}>
        <LogsPanel />
        <EventsPanel />
      </div>
      <div className="hub-collapse" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10 }}>
        <WatchdogPanel />
        <BackupsPanel />
      </div>

      <DepartmentObservability department="eng" />
      <CassMonitorPanel />
      <AgentRunsCard />

      {refreshedAt && (
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-dim)' }} className="mono">
          refreshed {formatTime(refreshedAt)}
        </div>
      )}
    </div>
  )
}

// ── KPI strip ──────────────────────────────────────────────────────────
function KPIStrip({ stats, pipeline }: { stats: HubStats | null; pipeline: HubMissionPipelineResponse | null }) {
  const [queueCounts, setQueueCounts] = useState<{ p0: number; p1: number; total: number } | null>(null)
  const [activeAlerts, setActiveAlerts] = useState(0)
  const [totalRules, setTotalRules] = useState(10)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [q, ha, hr] = await Promise.all([
        // Live queue is GitHub Issues (workflow_items archived 2026-05-23).
        hubGithubIssues.list({ status: 'queued' }).catch(() => null),
        hubHealth.alerts('active').catch(() => null),
        hubHealth.rules().catch(() => null),
      ])
      if (cancelled) return
      if (q) {
        const p0 = q.items.filter(i => i.priority_label === 'p0').length
        const p1 = q.items.filter(i => i.priority_label === 'p1').length
        setQueueCounts({ p0, p1, total: q.items.length })
      }
      if (ha) setActiveAlerts(ha.alerts.length)
      if (hr) setTotalRules(hr.rules.length || 10)
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const today = stats?.shipped_today ?? 0
  const yest = stats?.shipped_yesterday ?? 0
  const delta = today - yest
  const errToday = stats?.errors_today_open ?? 0
  const errLastHr = stats?.errors_last_hour ?? 0
  const p0p1 = (queueCounts?.p0 ?? 0) + (queueCounts?.p1 ?? 0)
  const totalQ = queueCounts?.total ?? 0
  const migration = stats?.pulse?.migration_version ?? '—'
  const env = stats?.pulse?.app_env || ''
  const uptime = stats?.pulse ? formatUptime(stats.pulse.uptime_seconds) : '—'
  // HUB-ENG-NORMALIZE (#1358): show the pipeline's authoritative "Coding" count
  // (status:in-progress MINUS review-waiting) — the SAME number Mission Control
  // shows — instead of the raw stats.active_claims, which included review-
  // waiting items and disagreed with MC's "Coding" tile. Falls back to '—'
  // while the pipeline loads.
  const coding = codingCount(pipeline)

  return (
    <Card bodyPad={false}>
      <div className="hub-collapse-kpi" style={{
        display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1,
        background: 'var(--border)',
      }}>
        <KPI
          label="Today's ships" value={today}
          delta={`${delta >= 0 ? '+' : ''}${delta} vs yest`}
          tone={delta >= 0 ? 'up' : 'down'}
          endpoint="/hub/stats"
        />
        <KPI
          label="Errors today" value={errToday}
          delta={`${errLastHr}/h current`}
          tone={errToday > 0 ? 'down' : 'flat'}
          endpoint="/hub/errors"
        />
        <KPI
          label="Queue P0/P1" value={p0p1}
          delta={`of ${totalQ} total`}
          tone={p0p1 > 0 ? 'down' : 'flat'}
          endpoint="/hub/github-issues"
        />
        <KPI
          label="Watchdog" value={`${totalRules - activeAlerts}/${totalRules}`}
          delta={activeAlerts > 0 ? `${activeAlerts} firing` : 'all ok'}
          tone={activeAlerts > 0 ? 'down' : 'up'}
          endpoint="/hub/health/alerts"
        />
        <KPI
          label="Migration" value={String(migration)}
          delta={env || ''}
          tone="flat"
          endpoint="/hub/stats"
        />
        <KPI
          label="Uptime" value={uptime}
          delta={coding != null ? `${coding} coding` : '— coding'}
          tone="flat"
          endpoint="/hub/mission/pipeline"
        />
      </div>
    </Card>
  )
}

// ── Ships panel ──────────────────────────────────────────────────────
function ShipsPanel({ stats }: { stats: HubStats | null }) {
  // HUB-REPOINT (#1217): ships now come from the live GitHub cache
  // (state=closed, ordered shipped_at DESC server-side) instead of the frozen
  // workflow_items queue, which reported "No ships in 24h" on days with deploys.
  const [recent, setRecent] = useState<GithubIssueItem[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubGithubIssues.list({ state: 'closed' }).catch(() => null)
      if (cancelled) return
      if (r) setRecent(r.items.filter(it => it.shipped_at))
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const series = useMemo(() => buildShipsSeries(recent), [recent])
  const recent24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600 * 1000
    return recent.filter(it => it.shipped_at && new Date(it.shipped_at).getTime() >= cutoff)
  }, [recent])
  const bundle = stats?.pulse?.bundle_hash ?? '—'

  return (
    <CollapsiblePanel
      title="Ships · 24h" endpoint="/hub/github-issues?state=closed"
      icon="package"
      right={<Pill variant={recent24h.length > 0 ? 'ok' : 'ghost'}>{recent24h.length} shipped</Pill>}
    >
      {series.some(v => v > 0) ? (
        <AreaChart
          data={series}
          color="var(--accent)"
          height={120}
          xLabels={['−24h', '−18h', '−12h', '−6h', 'now']}
        />
      ) : (
        <div style={{
          height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-dim)', fontSize: 11, border: '1px dashed var(--border)', borderRadius: 6,
        }}>
          No ships in last 24 hours
        </div>
      )}
      {bundle !== '—' && (
        <div className="row mono" style={{ marginTop: 6, fontSize: 10.5, color: 'var(--text-dim)' }}>
          <span>bundle {bundle}</span>
        </div>
      )}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 10, maxHeight: 360, overflowY: 'auto' }}>
        {recent24h.length === 0 ? (
          <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
            Nothing shipped in the last 24 hours.
          </div>
        ) : recent24h.map((it, i) => {
          const sha7 = it.merged_commit_sha ? it.merged_commit_sha.slice(0, 7) : ''
          const ts = it.shipped_at ?? it.issue_updated_at
          return (
            <div
              key={it.issue_number}
              style={{
                display: 'grid',
                gridTemplateColumns: '8px 1fr 70px',
                gap: 8,
                padding: '8px 0',
                borderTop: i ? '1px solid var(--border)' : 0,
                fontSize: 11.5,
                alignItems: 'flex-start',
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)',
                marginTop: 4, boxShadow: '0 0 0 2px color-mix(in oklch, var(--ok) 22%, transparent)',
              }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span style={{
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.45,
                }}>
                  {it.title}
                </span>
                <div className="row mono" style={{ gap: 10, fontSize: 10, color: 'var(--text-dim)', flexWrap: 'wrap' }}>
                  <span>#{it.issue_number}</span>
                  {sha7 && <span>{sha7}</span>}
                </div>
              </div>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', textAlign: 'right', marginTop: 2 }}>
                {relTime(ts)}
              </span>
            </div>
          )
        })}
      </div>
    </CollapsiblePanel>
  )
}

// ── Errors panel ──────────────────────────────────────────────────────
type ErrorStatusFilter = 'all' | 'open' | 'acknowledged' | 'resolved'

function ErrorsPanel() {
  const [errors, setErrors] = useState<HubErrorEvent[]>([])
  const [busy, setBusy] = useState<number | null>(null)
  const [filter, setFilter] = useState<ErrorStatusFilter>('open')

  const refresh = useCallback(async () => {
    const params: { status?: string; limit: number } = { limit: 200 }
    if (filter !== 'all') params.status = filter
    const r = await hubErrors.list(params).catch(() => null)
    if (r) setErrors(r.errors)
  }, [filter])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  // Bars chart + count pill always reflect OPEN errors regardless of filter.
  const [allOpen, setAllOpen] = useState<HubErrorEvent[]>([])
  useEffect(() => {
    const load = async () => {
      const r = await hubErrors.list({ status: 'open', limit: 200 }).catch(() => null)
      if (r) setAllOpen(r.errors)
    }
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [])

  const hourly = useMemo(() => bucketHourly(allOpen), [allOpen])
  const totalOpen = allOpen.length

  const ack = async (id: number) => {
    setBusy(id)
    try { await hubErrors.ack(id); posthogCapture('hub_error_acked', { error_id: id }); await refresh() } finally { setBusy(null) }
  }
  const resolve = async (id: number) => {
    setBusy(id)
    try { await hubErrors.resolve(id); posthogCapture('hub_error_resolved', { error_id: id }); await refresh() } finally { setBusy(null) }
  }

  const counts = useMemo(() => {
    // Quick local count — for the chip badges. The visible list is
    // already-filtered by the server, so we approximate from `allOpen`
    // (open count is exact; ack/resolved are unknown without a separate
    // call — leave undefined so the chip just doesn't show a number).
    return { open: allOpen.length }
  }, [allOpen])

  return (
    <CollapsiblePanel
      title="Errors · 24h" endpoint="/hub/errors"
      icon="alert-triangle"
      right={totalOpen > 0 ? <Pill variant="err">{totalOpen} open</Pill> : <Pill variant="ok">0 open</Pill>}
    >
      <Bars data={hourly} color="var(--err)" height={64} />
      <div className="row mono" style={{ justifyContent: 'space-between', fontSize: 9.5, color: 'var(--text-dim)', marginTop: 4 }}>
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
      </div>
      <div className="row" style={{ gap: 6, marginTop: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <PillFilter<ErrorStatusFilter>
          value={filter}
          onChange={setFilter}
          options={[
            ['open', `Open${counts.open ? ` (${counts.open})` : ''}`],
            ['acknowledged', 'Acknowledged'],
            ['resolved', 'Resolved'],
            ['all', 'All'],
          ]}
        />
      </div>
      <div style={{ borderTop: '1px solid var(--border)', maxHeight: 520, overflowY: 'auto' }}>
        {errors.length === 0 && (
          <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
            No errors match this filter.
          </div>
        )}
        {errors.slice(0, 50).map((e, i) => {
          const dotColor =
            e.status === 'open' ? 'var(--err)' :
            e.status === 'acknowledged' ? 'var(--warn)' :
            e.status === 'resolved' ? 'var(--ok)' :
            'var(--text-dim)'
          const stateNote =
            e.status === 'resolved' && e.resolved_at ? `resolved ${relTime(e.resolved_at)}` :
            e.status === 'acknowledged' && e.acked_at ? `acked ${relTime(e.acked_at)}` :
            null
          const isActioned = e.status === 'acknowledged' || e.status === 'resolved'
          return (
            <div
              key={e.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '8px 50px 1fr 70px',
                gap: 8,
                padding: '8px 0',
                borderTop: i ? '1px solid var(--border)' : 0,
                fontSize: 11.5,
                alignItems: 'flex-start',
                opacity: isActioned ? 0.65 : 1,
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: dotColor,
                marginTop: 4, boxShadow: `0 0 0 2px ${dotColor}22`,
              }} />
              <span className="mono" style={{ color: e.severity === 'critical' ? 'var(--err)' : 'var(--text-dim)' }}>
                #{e.id}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span style={{
                  color: 'var(--text)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.45,
                  textDecoration: e.status === 'resolved' ? 'line-through' : 'none',
                }}>
                  {e.error_message}
                </span>
                <div className="row mono" style={{ gap: 10, fontSize: 10, color: 'var(--text-dim)', flexWrap: 'wrap' }}>
                  <span>{e.method} {e.endpoint}</span>
                  {e.error_code !== 0 && <span>{e.error_code}</span>}
                  {e.severity && <span>{e.severity}</span>}
                  {stateNote && <span style={{ color: dotColor }}>{stateNote}</span>}
                </div>
                {e.status === 'open' && (
                  <div className="row" style={{ gap: 6, marginTop: 2 }}>
                    <button
                      className="btn ghost"
                      onClick={() => ack(e.id)}
                      disabled={busy === e.id}
                      style={{ fontSize: 10.5, padding: '2px 8px' }}
                    >ack</button>
                    <button
                      className="btn ghost"
                      onClick={() => resolve(e.id)}
                      disabled={busy === e.id}
                      style={{ fontSize: 10.5, padding: '2px 8px' }}
                    >resolve</button>
                  </div>
                )}
              </div>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', textAlign: 'right', marginTop: 2 }}>
                {relTime(e.created_at)}
              </span>
            </div>
          )
        })}
        {errors.length > 50 && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>
            showing top 50 of {errors.length}
          </div>
        )}
      </div>
    </CollapsiblePanel>
  )
}

// ── SIWA events panel — SPEC-SIWA-DIAGNOSTIC-SURFACE ─────────────────
function SiwaEventsPanel() {
  const [events, setEvents] = useState<SiwaEvent[]>([])
  const [since, setSince] = useState('')

  const refresh = useCallback(async () => {
    const r = await hubSiwaTrace(since || undefined).catch(() => null)
    if (r) setEvents(r.events.slice().reverse().slice(0, 100))
  }, [since])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 5_000)
    return () => clearInterval(id)
  }, [refresh])

  const errCount = events.filter(e => e.branch?.startsWith('error')).length

  return (
    <CollapsiblePanel
      title={`SIWA events · ${events.length}`}
      endpoint="/hub/siwa-trace"
      icon="shield"
      right={errCount > 0 ? <Pill variant="err">{errCount} errors</Pill> : <Pill variant={events.length > 0 ? 'live' : 'ghost'}>{events.length}</Pill>}
    >
      <div style={{ marginBottom: 8 }}>
        <input
          placeholder="since (RFC3339, e.g. 2026-05-31T21:00:00Z)"
          value={since}
          onChange={e => setSince(e.target.value)}
          style={{
            width: '100%', padding: '5px 10px', borderRadius: 6,
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            color: 'var(--text)', fontSize: 12, fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
      </div>
      {events.length === 0 ? (
        <div style={{ padding: '16px 0', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
          No SIWA events buffered — ring buffer populates on first SIWA flow.
        </div>
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {events.map((e, i) => {
            const isErr = e.branch?.startsWith('error') ?? false
            return (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px 120px 1fr 50px',
                  gap: 8,
                  padding: '6px 0',
                  borderTop: i ? '1px solid var(--border)' : 0,
                  fontSize: 11.5,
                  alignItems: 'flex-start',
                }}
              >
                <span className="mono" style={{ color: 'var(--text-dim)', fontSize: 10.5 }}>
                  {e.ts.slice(11, 19)}
                </span>
                <span className="mono" style={{
                  color: isErr ? 'var(--err)' : 'var(--info)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: 600, fontSize: 10.5,
                }}>
                  {e.branch ? `[${e.branch}]` : ''}
                </span>
                <span style={{ color: 'var(--text)', wordBreak: 'break-word', lineHeight: 1.4, minWidth: 0 }}>
                  {e.message.replace(/^SIWA\[[^\]]+\]\s*/, '')}
                </span>
                {e.is_native && (
                  <span style={{ fontSize: 10, color: 'var(--accent)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    native
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </CollapsiblePanel>
  )
}

// ── GitHub Issues panel — TENON-MIGRATION-2 ──────────────────────────
type GHIssueStatus = 'all' | 'queued' | 'in-progress' | 'blocked' | 'done' | 'deployed'
type GHIssuePriority = 'all' | 'p0' | 'p1' | 'p2' | 'p3'

function GitHubIssuesPanel({ pipeline }: { pipeline: HubMissionPipelineResponse | null }) {
  const [items, setItems] = useState<GithubIssueItem[]>([])
  const [status, setStatus] = useState<GHIssueStatus>('queued')
  const [prio, setPrio] = useState<GHIssuePriority>('all')
  const [search, setSearch] = useState('')
  const [lastSync, setLastSync] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [drawerItem, setDrawerItem] = useState<GithubIssueItem | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)

  const refresh = useCallback(async () => {
    const params: { status?: string; priority?: string; state?: string } = {}
    if (status !== 'all' && status !== 'deployed') params.status = status
    if (prio !== 'all') params.priority = prio
    // 'deployed' is a client-side view of closed issues — back-end filter is same as done.
    params.state = (status === 'done' || status === 'deployed') ? 'closed' : status === 'all' ? 'all' : 'open'
    const r = await hubGithubIssues.list(params).catch(() => null)
    if (r) {
      setItems(r.items)
      if (r.items.length > 0) {
        setLastSync(r.items[0].last_synced_at)
      }
    }
  }, [status, prio])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  const triggerSync = async () => {
    setSyncing(true)
    await hubGithubIssues.sync().catch(() => null)
    setTimeout(() => { refresh(); setSyncing(false) }, 2000)
  }

  const openDrawer = async (it: GithubIssueItem) => {
    // Use cached body if available, otherwise fetch full row.
    if (it.body) {
      setDrawerItem(it)
      return
    }
    setDrawerLoading(true)
    const full = await hubGithubIssues.get(it.issue_number).catch(() => null)
    setDrawerLoading(false)
    setDrawerItem(full ?? it)
  }

  // Counts mirror the old QueuePanel: P0/P1/P2/P3 pills in the header.
  const counts = useMemo(() => {
    const c = { p0: 0, p1: 0, p2: 0, p3: 0 }
    for (const it of items) {
      if (it.priority_label === 'p0') c.p0++
      else if (it.priority_label === 'p1') c.p1++
      else if (it.priority_label === 'p2') c.p2++
      else if (it.priority_label === 'p3') c.p3++
    }
    return c
  }, [items])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(it =>
      it.title.toLowerCase().includes(q) ||
      (it.project_label ?? '').toLowerCase().includes(q) ||
      (it.type_label ?? '').toLowerCase().includes(q) ||
      (it.body_summary ?? '').toLowerCase().includes(q) ||
      String(it.issue_number).includes(q)
    )
  }, [items, search])

  const PRIO_COLOR: Record<string, string> = {
    p0: 'var(--danger)', p1: 'var(--warning)', p2: 'var(--text-dim)', p3: 'var(--text-dim)',
  }
  const SAFETY_DOT: Record<string, string> = {
    green: '#22c55e', yellow: '#f59e0b', red: '#ef4444', blocked: '#6b7280',
  }

  // Show Gate column only in Queued/Blocked views where it's actionable.
  const showGateCol = status === 'queued' || status === 'blocked' || status === 'all'
  // Show deploy status column in Done/Deployed views.
  const showDeployCol = status === 'done' || status === 'deployed' || status === 'all'

  return (
    <>
      <CollapsiblePanel
        title={`Queue · ${filteredItems.length} item${filteredItems.length === 1 ? '' : 's'}`}
        endpoint="/hub/github-issues"
        icon="git-branch"
        right={
          <div className="row" style={{ gap: 4, alignItems: 'center' }}>
            {(['p0', 'p1', 'p2', 'p3'] as const).map(p => (
              <span key={p} className="pill ghost" style={{ fontSize: 9.5 }}>
                {p.toUpperCase()}: {counts[p]}
              </span>
            ))}
            <button
              onClick={triggerSync}
              disabled={syncing}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-dim)', fontSize: 11, padding: '2px 6px', marginLeft: 4,
              }}
              title="Re-sync from GitHub API"
            >
              {syncing ? '⟳ syncing…' : '⟳ sync'}
            </button>
          </div>
        }
      >
        <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <PillFilter<GHIssuePriority>
            value={prio}
            onChange={setPrio}
            options={[['all', 'All'], ['p0', 'P0'], ['p1', 'P1'], ['p2', 'P2'], ['p3', 'P3']]}
          />
          <PillFilter<GHIssueStatus>
            value={status}
            onChange={setStatus}
            options={[
              ['all', 'All'],
              ['queued', 'Queued'],
              ['in-progress', 'In Progress'],
              ['blocked', 'Blocked'],
              ['done', 'Done'],
              ['deployed', 'Live'],
            ]}
          />
          <input
            placeholder="Search title / body / project / #"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, minWidth: 220, padding: '5px 10px', borderRadius: 6,
              background: 'var(--surface-2)', border: '1px solid var(--border)',
              color: 'var(--text)', fontSize: 12, fontFamily: 'inherit',
            }}
          />
          {lastSync && (
            <span style={{ fontSize: 10, color: 'var(--text-dim)', alignSelf: 'center' }}>
              synced {formatTime(new Date(lastSync))}
            </span>
          )}
        </div>
        {filteredItems.length === 0 ? (
          <div style={{ padding: '16px 0', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
            {search.trim() ? 'No issues match the search.' :
              status === 'queued' ? 'No queued issues. Cache may be empty — click ⟳ sync to populate.' :
              'No issues match these filters.'}
          </div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 26 }}></th>{/* heartbeat dot */}
                  <th style={{ width: 40 }}>Prio</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 50 }}>#</th>
                  <th>Title</th>
                  <th style={{ width: 80 }}>Project</th>
                  {showGateCol && <th style={{ width: 110 }}>Gate</th>}
                  {showDeployCol && <th style={{ width: 90 }}>Deploy</th>}
                  <th style={{ width: 60, textAlign: 'right' }}>Age</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.slice(0, 100).map(it => {
                  const isShippedView = status === 'done' || status === 'deployed'
                  const rightTs = isShippedView ? (it.shipped_at ?? it.issue_updated_at) : it.issue_updated_at
                  const hbDot = heartbeatDot(it.last_heartbeat_at)
                  const deployBadge = getDeployBadge(it)
                  return (
                    <tr
                      key={it.issue_number}
                      onClick={() => openDrawer(it)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ textAlign: 'center', paddingRight: 0 }}>
                        {hbDot && (
                          <span
                            title={`heartbeat: ${it.last_heartbeat_at ? relTime(it.last_heartbeat_at) + ' ago' : 'never'}`}
                            style={{
                              display: 'inline-block',
                              width: 7, height: 7, borderRadius: '50%',
                              background: hbDot,
                              boxShadow: hbDot === '#22c55e' ? '0 0 0 2px #22c55e33' : 'none',
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <span style={{
                          fontWeight: 700, fontSize: 10,
                          color: PRIO_COLOR[it.priority_label] || 'var(--text-dim)',
                        }}>
                          {it.priority_label?.toUpperCase() || '—'}
                        </span>
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {(it.status_label === 'in-progress' || it.status_label === 'in_progress') ? (
                            (() => {
                              const stage = inProgressStage(it.issue_number, pipeline)
                              const chip = (
                                <Pill variant={stage.unknown ? 'ghost' : stage.prNumber ? 'warn' : 'info'} style={{ fontSize: 9.5 }}>
                                  {stage.unknown ? `${stage.label} · stage unknown` : stage.label}
                                </Pill>
                              )
                              return stage.prNumber ? (
                                <a
                                  href={`${GITHUB_REPO_PR_BASE}/${stage.prNumber}`}
                                  target="_blank" rel="noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  style={{ textDecoration: 'none' }}
                                >
                                  {chip}
                                </a>
                              ) : chip
                            })()
                          ) : (
                            <span className={`pill ${it.status_label === 'queued' ? 'ghost' : it.status_label === 'blocked' ? 'danger' : 'success'}`}
                              style={{ fontSize: 9.5 }}>
                              {it.status_label || it.state}
                            </span>
                          )}
                          {it.safety_label && it.safety_label !== 'green' && (
                            <span title={`safety:${it.safety_label}`}
                              style={{
                                width: 6, height: 6, borderRadius: '50%',
                                background: SAFETY_DOT[it.safety_label] || '#6b7280',
                                display: 'inline-block', flexShrink: 0,
                              }} />
                          )}
                        </span>
                      </td>
                      <td>
                        <a
                          href={it.github_url} target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: 'var(--text-dim)', fontSize: 11 }}
                        >
                          #{it.issue_number}
                        </a>
                      </td>
                      <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: 12 }}>
                        {it.title}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {it.project_label || '—'}
                      </td>
                      {showGateCol && (
                        <td style={{ fontSize: 10, color: 'var(--warn)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>
                          {it.gate || ''}
                        </td>
                      )}
                      {showDeployCol && (
                        <td className="mono" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                          {deployBadge}
                        </td>
                      )}
                      <td className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', textAlign: 'right' }}>
                        {ageFromIso(rightTs)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {drawerLoading && (
          <div style={{ padding: 8, fontSize: 11, color: 'var(--text-dim)' }}>Loading…</div>
        )}
      </CollapsiblePanel>

      {/* Inline drawer — opens below the panel when a row is clicked */}
      {drawerItem && (
        <GithubIssueDrawer
          item={drawerItem}
          onClose={() => setDrawerItem(null)}
        />
      )}
    </>
  )
}

// ── GitHub Issue drawer ───────────────────────────────────────────────
function GithubIssueDrawer({ item, onClose }: { item: GithubIssueItem; onClose: () => void }) {
  const deployBadge = getDeployBadge(item)
  const labels: Array<{ name: string }> = Array.isArray(item.labels) ? item.labels : []

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 16,
      position: 'relative',
    }}>
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 10, right: 12,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-dim)', fontSize: 16, lineHeight: 1,
        }}
        title="Close"
      >✕</button>

      {/* Header */}
      <div style={{ marginBottom: 10, paddingRight: 24 }}>
        <a
          href={item.github_url}
          target="_blank"
          rel="noreferrer"
          style={{ color: 'var(--accent)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
        >
          #{item.issue_number} {item.title}
        </a>
        <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {labels.map(l => (
            <span key={l.name} className="pill ghost" style={{ fontSize: 9.5 }}>{l.name}</span>
          ))}
          {deployBadge && (
            <span className="mono" style={{ fontSize: 10, marginLeft: 4 }}>{deployBadge}</span>
          )}
          {item.model_hint && (
            <span className="pill ghost" style={{ fontSize: 9.5 }}>{item.model_hint}</span>
          )}
        </div>
        {item.gate && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--warn)', fontStyle: 'italic' }}>
            Gate: {item.gate}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '10px 12px',
        maxHeight: 360,
        overflowY: 'auto',
        fontSize: 11.5,
        lineHeight: 1.6,
        color: 'var(--text)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'var(--font-mono)',
      }}>
        {item.body || item.body_summary || <span style={{ color: 'var(--text-dim)' }}>No body cached — click the issue link to view on GitHub.</span>}
      </div>

      {/* Footer */}
      <div className="row" style={{ marginTop: 8, gap: 12, fontSize: 10, color: 'var(--text-dim)' }}>
        <span>created {ageFromIso(item.issue_created_at)} ago</span>
        {item.shipped_at && <span>shipped {ageFromIso(item.shipped_at)} ago</span>}
        {item.deployed_at && <span>deployed {ageFromIso(item.deployed_at)} ago</span>}
        {item.last_heartbeat_at && <span>heartbeat {relTime(item.last_heartbeat_at)} ago</span>}
        <a href={item.github_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', marginLeft: 'auto' }}>
          Open on GitHub ↗
        </a>
      </div>
    </div>
  )
}

// ── Heartbeat dot helper ──────────────────────────────────────────────
// Returns a CSS colour string, or null when heartbeat is not applicable.
function heartbeatDot(lastHeartbeatAt: string | null | undefined): string | null {
  if (!lastHeartbeatAt) return null
  const ageMs = Date.now() - new Date(lastHeartbeatAt).getTime()
  if (!Number.isFinite(ageMs) || ageMs < 0) return null
  const ageMin = ageMs / 60_000
  if (ageMin < 5) return '#22c55e'    // green — fresh (<5 min)
  if (ageMin < 15) return '#f59e0b'   // amber — ageing (5–15 min)
  return '#ef4444'                    // red — stale (>15 min)
}

// ── Deploy badge helper ───────────────────────────────────────────────
function getDeployBadge(it: GithubIssueItem): React.ReactNode | null {
  if (it.deployed_at) {
    return <span style={{ color: 'var(--ok)' }}>✓ live</span>
  }
  if (it.merged_commit_sha) {
    return <span style={{ color: 'var(--warn)' }}>⏱ deploying</span>
  }
  if (it.state === 'closed' && !it.shipped_at && !it.merged_commit_sha) {
    return <span style={{ color: 'var(--text-dim)' }}>doc-only</span>
  }
  return null
}

// ── Helpers shared by the GitHub Issues panel ─────────────────────────────────
function ageFromIso(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const m = Math.floor(ms / 60_000)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

// ── Queue panel ──────────────────────────────────────────────────────
type QueueStatus = 'all' | 'queued' | 'preparing' | 'in_progress' | 'blocked' | 'done' | 'deployed'
type QueuePrio = 'all' | 'p0' | 'p1' | 'p2' | 'p3'

const SINCE_OPTIONS: Array<{ value: '5m' | '15m' | '1h' | '6h'; label: string }> = [
  { value: '5m', label: '5m' },
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
]
const LEVEL_OPTIONS: Array<{ value: 'all' | 'error' | 'warn' | 'info'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warn' },
  { value: 'info', label: 'Info' },
]

function LogsPanel() {
  const [since, setSince] = useState<'5m' | '15m' | '1h' | '6h'>('15m')
  const [level, setLevel] = useState<'all' | 'error' | 'warn' | 'info'>('all')
  const [search, setSearch] = useState('')
  const [entries, setEntries] = useState<HubLogEntry[]>([])
  const [paused, setPaused] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (paused) return
    const r = await hubLogs.search({
      q: search.trim() || undefined,
      since,
      level: level === 'all' ? undefined : level,
      limit: 100,
    }).catch(() => null)
    if (r) {
      setEntries(r.entries)
      setNote(r.note || null)
    }
  }, [since, level, search, paused])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 5_000)
    return () => clearInterval(id)
  }, [refresh])

  return (
    <CollapsiblePanel
      title="Logs · live" endpoint="/hub/logs/search" icon="activity"
      right={
        <button
          className="btn ghost"
          onClick={() => setPaused(p => !p)}
          style={{ fontSize: 11, padding: '2px 8px' }}
          title={paused ? 'Resume tail' : 'Pause tail'}
        >
          <Icon name={paused ? 'play' : 'pause'} size={11} />
          <span style={{ marginLeft: 4 }}>{paused ? 'paused' : 'live'}</span>
        </button>
      }
      bodyPad={false}
    >
      <div className="row" style={{ gap: 6, padding: '8px 12px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <PillFilter<'5m' | '15m' | '1h' | '6h'>
          value={since}
          onChange={setSince}
          options={SINCE_OPTIONS.map(o => [o.value, o.label])}
        />
        <PillFilter<'all' | 'error' | 'warn' | 'info'>
          value={level}
          onChange={setLevel}
          options={LEVEL_OPTIONS.map(o => [o.value, o.label])}
        />
        <input
          placeholder="filter…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: 140, padding: '4px 8px', borderRadius: 6,
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            color: 'var(--text)', fontSize: 11, fontFamily: 'var(--font-mono)',
          }}
        />
      </div>
      <div style={{
        background: 'var(--bg)', fontFamily: 'var(--font-mono)',
        fontSize: 10.5, padding: '8px 12px',
        maxHeight: 280, overflow: 'auto', lineHeight: 1.55,
      }}>
        {note && (
          <div style={{ color: 'var(--warn)', fontSize: 10.5, marginBottom: 6 }}>{note}</div>
        )}
        {entries.length === 0 && !note && (
          <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 16 }}>
            No log entries in the last {since}.
          </div>
        )}
        {entries.map((l, i) => {
          const lvl = (l.level || '').toUpperCase().slice(0, 4).padEnd(4, ' ')
          const lvlColor = lvl.startsWith('ERR') ? 'var(--err)'
            : lvl.startsWith('WARN') ? 'var(--warn)'
            : 'var(--info)'
          const time = (l.ts || l.journal_ts || '').slice(11, 19)
          return (
            <div key={i} className="row" style={{ gap: 8, padding: '1px 0', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--text-dim)', width: 60, flexShrink: 0 }}>{time}</span>
              <span style={{ width: 36, color: lvlColor, flexShrink: 0 }}>{lvl}</span>
              <span style={{ width: 80, color: 'var(--text-muted)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.handler || l.unit || ''}
              </span>
              <span style={{ color: 'var(--text)', flex: 1, wordBreak: 'break-word' }}>
                {l.msg || l.error || l.raw || ''}
              </span>
            </div>
          )
        })}
      </div>
    </CollapsiblePanel>
  )
}

// ── Events panel ─────────────────────────────────────────────────────
function EventsPanel() {
  const [events, setEvents] = useState<HubWebhookEvent[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubWebhooks.list({ limit: 30 }).catch(() => null)
      if (cancelled) return
      if (r) setEvents(r.events)
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  return (
    <CollapsiblePanel
      title="Events · audit stream" endpoint="/hub/webhooks" icon="zap"
      bodyPad={false}
    >
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        {events.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
            No webhook events yet.
          </div>
        )}
        {events.map((e, i) => {
          const tone = e.error_message
            ? 'err'
            : e.signature_valid === false
              ? 'warn'
              : 'ok'
          return (
            <div
              key={e.id}
              className="row"
              style={{
                padding: '7px 12px',
                borderTop: i ? '1px solid var(--border)' : 0,
                gap: 8, fontSize: 11.5,
              }}
            >
              <span className="mono" style={{ width: 32, color: 'var(--text-dim)', flexShrink: 0 }}>
                {relTime(e.received_at)}
              </span>
              <StatusDot tone={tone as 'ok' | 'warn' | 'err'} />
              <span className="mono" style={{
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', minWidth: 0,
              }}>
                {e.source}.{e.event_type}
              </span>
              <span className="mono" style={{
                fontSize: 10.5, color: 'var(--text-dim)', flexShrink: 0,
                maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {e.matched_row_id || (e.status_code >= 400 ? `HTTP ${e.status_code}` : '')}
              </span>
            </div>
          )
        })}
      </div>
    </CollapsiblePanel>
  )
}

// ── Watchdog panel ────────────────────────────────────────────────────
function WatchdogPanel() {
  const [alerts, setAlerts] = useState<HubHealthAlert[]>([])
  const [rules, setRules] = useState<HubHealthRule[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)

  const refresh = useCallback(async () => {
    const [a, r] = await Promise.all([
      hubHealth.alerts('active').catch(() => null),
      hubHealth.rules().catch(() => null),
    ])
    if (a) setAlerts(a.alerts)
    if (r) setRules(r.rules)
  }, [])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  const ack = async (id: number) => { setBusyId(id); try { await hubHealth.ack(id); posthogCapture('hub_health_alert_acked', { alert_id: id }); await refresh() } finally { setBusyId(null) } }
  const resolve = async (id: number) => { setBusyId(id); try { await hubHealth.resolve(id); posthogCapture('hub_health_alert_resolved', { alert_id: id }); await refresh() } finally { setBusyId(null) } }

  const known = new Set(RULE_ORDER)
  const extras = rules.map(r => r.name).filter(n => !known.has(n))
  const ordered = [...RULE_ORDER, ...extras]
  const ruleByName = useMemo(() => new Map(rules.map(r => [r.name, r])), [rules])
  const alertByRule = useMemo(() => new Map(alerts.map(a => [a.rule_name, a])), [alerts])

  const activeCount = alerts.length

  return (
    <CollapsiblePanel
      title={`Watchdog · ${ordered.length} invariants`}
      endpoint="/hub/health/rules" icon="shield"
      right={activeCount > 0 ? <Pill variant="warn">{activeCount} active</Pill> : <Pill variant="ok">all ok</Pill>}
      bodyPad={false}
    >
      <div className="hub-collapse-kpi" style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1,
        background: 'var(--border)', borderBottomLeftRadius: 9, borderBottomRightRadius: 9, overflow: 'hidden',
      }}>
        {ordered.map(name => {
          const alert = alertByRule.get(name)
          const rule = ruleByName.get(name)
          const tone: 'ok' | 'warn' | 'err' = !alert
            ? 'ok'
            : alert.severity === 'critical' ? 'err' : 'warn'
          return (
            <div key={name} style={{
              background: 'var(--surface)', padding: '10px 12px',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <div className="row">
                <StatusDot tone={tone} />
                <span className="mono" style={{
                  fontSize: 11, color: tone === 'err' ? 'var(--err)'
                    : tone === 'warn' ? 'var(--warn)' : 'var(--text)',
                  marginLeft: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {name}
                </span>
              </div>
              <span className="mono" style={{
                fontSize: 10, color: 'var(--text-dim)', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {alert
                  ? `${alert.title || alert.dedup_key} · ${relTime(alert.last_seen_at)}`
                  : rule?.threshold || rule?.description || 'ok'}
              </span>
              {alert && (
                <div className="row" style={{ gap: 4, marginTop: 2 }}>
                  <button
                    className="btn ghost"
                    onClick={() => ack(alert.id)}
                    disabled={busyId === alert.id}
                    style={{ fontSize: 10, padding: '1px 6px' }}
                  >ack</button>
                  <button
                    className="btn ghost"
                    onClick={() => resolve(alert.id)}
                    disabled={busyId === alert.id}
                    style={{ fontSize: 10, padding: '1px 6px' }}
                  >resolve</button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </CollapsiblePanel>
  )
}

// ── Backups panel (HUB-12-11) ─────────────────────────────────────────
// DO managed-DB snapshot visibility. Reads /hub/backups which proxies the
// Digital Ocean API. Soft-fails to a "DO_API_TOKEN missing" message when
// the env var isn't set on the VPS.
function BackupsPanel() {
  const [backups, setBackups] = useState<HubBackup[]>([])
  const [source, setSource] = useState<'do' | 'unavailable' | null>(null)
  const [note, setNote] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubBackups().catch(() => null)
      if (cancelled) return
      if (r) {
        setBackups(r.backups)
        setSource(r.source)
        setNote(r.note || '')
      }
    }
    load()
    const id = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const newest = backups[0]
  const ageHours = newest?.taken_at ? hoursSince(newest.taken_at) : null
  const headerTone: 'ok' | 'warn' | 'err' = source === 'unavailable'
    ? 'warn'
    : ageHours !== null && ageHours > 30
      ? 'warn'
      : 'ok'

  return (
    <CollapsiblePanel
      title={`Backups · ${backups.length} snapshot${backups.length === 1 ? '' : 's'}`}
      endpoint="/hub/backups" icon="database"
      right={
        <StatusDot tone={headerTone} />
      }
      bodyPad={false}
    >
      {source === 'unavailable' && (
        <div style={{ padding: 14, fontSize: 12, color: 'var(--text-dim)' }}>
          {note || 'DO API unavailable.'}
        </div>
      )}
      {source === 'do' && backups.length === 0 && (
        <div style={{ padding: 14, fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
          No managed-DB snapshots reported by DigitalOcean.
        </div>
      )}
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        {backups.map((b, i) => (
          <div
            key={`${b.cluster_id}-${b.taken_at}-${i}`}
            className="row"
            style={{
              padding: '7px 12px',
              borderTop: i ? '1px solid var(--border)' : 0,
              gap: 8, fontSize: 11.5,
            }}
          >
            <span className="mono" style={{ width: 60, color: 'var(--text-dim)', flexShrink: 0 }}>
              {b.taken_at ? relTime(b.taken_at) : '—'}
            </span>
            <span className="mono" style={{
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', minWidth: 0,
            }}>
              {b.cluster_name || b.cluster_id}
            </span>
            <span className="mono" style={{
              fontSize: 10.5, color: 'var(--text-dim)', flexShrink: 0,
            }}>
              {b.size_gigabytes ? `${b.size_gigabytes.toFixed(2)} GB` : ''}
            </span>
          </div>
        ))}
      </div>
    </CollapsiblePanel>
  )
}

function hoursSince(iso: string): number | null {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return (Date.now() - t) / 3_600_000
}

// ── Shared atoms (panel + filters) ───────────────────────────────────
function CollapsiblePanel({
  title, endpoint, icon, right, bodyPad = true, children,
}: {
  title: string
  endpoint?: string
  icon?: IconName
  right?: React.ReactNode
  bodyPad?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)
  return (
    <Card bodyPad={false}>
      <div
        className="head"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={11} style={{ color: 'var(--text-dim)' }} />
        {icon && <Icon name={icon} size={13} />}
        <span className="title">{title}</span>
        <span className="grow" />
        {right}
        {endpoint && <Endpoint>{endpoint}</Endpoint>}
      </div>
      {open && (bodyPad ? <div className="body">{children}</div> : children)}
    </Card>
  )
}

function PillFilter<T extends string>({
  value, onChange, options,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<[T, string]>
}) {
  return (
    <div className="row" style={{
      gap: 0, padding: 2, borderRadius: 6,
      background: 'var(--surface-2)', border: '1px solid var(--border)',
    }}>
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          style={{
            padding: '3px 9px', borderRadius: 4, border: 'none',
            background: value === v ? 'var(--surface)' : 'transparent',
            color: value === v ? 'var(--text)' : 'var(--text-muted)',
            fontFamily: 'inherit', fontSize: 11, fontWeight: 500, cursor: 'pointer',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ── TriggersPanel — AUTONOMY-HUB-ENGINEERING-RELEASE-BUTTONS ──────────────
function TriggersPanel() {
  const [status, setStatus] = useState<TriggerStatus | null>(null)
  const [spawnRequests, setSpawnRequests] = useState<SpawnRequest[]>([])
  const [activeSessions, setActiveSessions] = useState(0)
  const [firing, setFiring] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<{ action: string; ok: boolean; msg: string } | null>(null)

  const loadStatus = useCallback(async () => {
    const [s, sr] = await Promise.all([
      hubTriggers.status().catch(() => null),
      hubSpawnRequest.list().catch(() => null),
    ])
    setStatus(s)
    if (sr) setSpawnRequests(sr.requests)
  }, [])

  useEffect(() => {
    loadStatus()
    const id = setInterval(loadStatus, 30_000)
    return () => clearInterval(id)
  }, [loadStatus])

  const fire = async (action: string, fn: () => Promise<unknown>) => {
    if (firing) return
    setFiring(action)
    setLastResult(null)
    try {
      await fn()
      setLastResult({ action, ok: true, msg: 'Queued successfully' })
      setTimeout(loadStatus, 3000)
    } catch (e: unknown) {
      setLastResult({ action, ok: false, msg: e instanceof Error ? e.message : 'Error' })
    } finally {
      setFiring(null)
    }
  }

  const runInfo = (runs: WorkflowRunSummary[] | undefined) => {
    if (!runs || runs.length === 0) return null
    const latest = runs[0]
    const icon = latest.status === 'completed'
      ? (latest.conclusion === 'success' ? '✓' : '✗')
      : '⟳'
    const age = Math.round((Date.now() - new Date(latest.created_at).getTime()) / 60000)
    return `${icon} ${latest.conclusion ?? latest.status} · ${age}m ago`
  }

  const spawnDisabled = activeSessions > 0 || firing === 'spawn'

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Endpoint>/hub/triggers/*</Endpoint>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Triggers</span>
      </div>
      <div className="hub-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>

        {/* Ship DMG */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Ship DMG</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            Builds + signs + notarizes + uploads to usetenon.com/downloads
          </div>
          <button
            disabled={firing === 'desktop'}
            onClick={() => {
              if (!window.confirm('Run release-desktop now? Builds + signs + notarizes DMG and updates usetenon.com/downloads.')) return
              fire('desktop', () => hubTriggers.desktopRelease())
            }}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              fontSize: 12,
              cursor: firing === 'desktop' ? 'not-allowed' : 'pointer',
              opacity: firing === 'desktop' ? 0.6 : 1,
              width: '100%',
            }}
          >
            {firing === 'desktop' ? 'Queuing…' : 'Ship DMG'}
          </button>
          {status?.desktop && (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }} className="mono">
              {runInfo(status.desktop.runs) ?? '—'}
              {status.desktop.error && <span style={{ color: 'var(--red)' }}> ({status.desktop.error})</span>}
            </div>
          )}
        </div>

        {/* Ship Mobile */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Ship Mobile</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            Bumps build + uploads to TestFlight
          </div>
          <button
            disabled={firing === 'ios'}
            onClick={() => {
              if (!window.confirm('Run release-ios now? Bumps build number and uploads to TestFlight.')) return
              fire('ios', () => hubTriggers.iosRelease())
            }}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              fontSize: 12,
              cursor: firing === 'ios' ? 'not-allowed' : 'pointer',
              opacity: firing === 'ios' ? 0.6 : 1,
              width: '100%',
            }}
          >
            {firing === 'ios' ? 'Queuing…' : 'Ship Mobile'}
          </button>
          {status?.ios && (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }} className="mono">
              {runInfo(status.ios.runs) ?? '—'}
              {status.ios.error && <span style={{ color: 'var(--red)' }}> ({status.ios.error})</span>}
            </div>
          )}
        </div>

        {/* Trigger Spawn */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Trigger Spawn</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            Force-start a new spawn session (Mac watcher picks up within ~60s)
          </div>
          <button
            disabled={spawnDisabled}
            onClick={() => {
              if (!window.confirm('Queue a forced spawn? The Mac watcher will start a new coding session within ~60s.')) return
              fire('spawn', () => hubSpawnRequest.post())
            }}
            style={{
              background: spawnDisabled ? 'var(--surface-2)' : 'var(--accent)',
              color: spawnDisabled ? 'var(--text-muted)' : '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              fontSize: 12,
              cursor: spawnDisabled ? 'not-allowed' : 'pointer',
              width: '100%',
            }}
          >
            {firing === 'spawn' ? 'Queuing…' : activeSessions > 0 ? `${activeSessions} session(s) running` : 'Trigger Spawn'}
          </button>
          {spawnRequests.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {spawnRequests.slice(0, 3).map(req => (
                <div key={req.id} style={{ fontSize: 10, color: 'var(--text-dim)', lineHeight: 1.6 }} className="mono">
                  #{req.id} {req.fired_at ? '✓ fired' : req.canceled_at ? '✗ canceled' : '⟳ pending'}
                  {' '}· {formatTime(new Date(req.requested_at))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {lastResult && (
        <div style={{
          marginTop: 10, padding: '6px 10px', borderRadius: 4,
          background: lastResult.ok ? 'var(--green-dim)' : 'var(--red-dim)',
          color: lastResult.ok ? 'var(--green)' : 'var(--red)',
          fontSize: 12,
        }}>
          {lastResult.action}: {lastResult.msg}
        </div>
      )}
    </Card>
  )
}

// ── AgentRunsCard — kill switch toggle + run history ─────────────────
// ── Cass monitor panel ───────────────────────────────────────────────
// Operator-facing view of the ship-reviewer cron lifecycle:
//   • Countdown to next 1800s tick
//   • Pending review backlog (deploy gate)
//   • Last session outcome + duration
//   • Recent verdict history (passed / partial / phantom / skipped)
function CassMonitorPanel() {
  const [data, setData] = useState<CassStatus | null>(null)
  const [eta, setEta] = useState<number>(0)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const s = await hubCassStatus().catch(() => null)
      if (cancelled) return
      if (s) { setData(s); setEta(s.next_eta_seconds); setRefreshedAt(new Date()) }
    }
    load()
    const id = setInterval(load, 15_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  useEffect(() => {
    const id = setInterval(() => setEta(s => Math.max(0, s - 1)), 1000)
    return () => clearInterval(id)
  }, [])

  const interval = data?.interval_seconds ?? 1800
  const elapsed = interval - eta
  const pct = Math.max(0, Math.min(100, (elapsed / interval) * 100))

  const etaLabel = (() => {
    if (eta <= 0) return 'firing now'
    const m = Math.floor(eta / 60)
    const s = eta % 60
    return `${m}:${String(s).padStart(2, '0')}`
  })()

  const last = data?.last_session
  const lastDuration = last?.duration_seconds != null
    ? (last.duration_seconds < 60 ? `${last.duration_seconds}s` : `${Math.round(last.duration_seconds / 60)}m`)
    : (last?.status === 'running' ? 'in flight' : '—')

  return (
    <CollapsiblePanel
      title="Cass (ship-reviewer)"
      icon="shield"
      endpoint="/hub/cass/status"
      right={
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          {refreshedAt && `refreshed ${formatTime(refreshedAt)}`}
        </span>
      }
    >
      {!data ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>loading…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={{ padding: 10, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Next tick</div>
              <div className="mono" style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, marginTop: 4 }}>{etaLabel}</div>
              <div style={{ marginTop: 6, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct > 90 ? 'var(--ok)' : 'var(--accent)', transition: 'width 1s linear' }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>interval {interval}s · {Math.round(pct)}% elapsed</div>
            </div>

            <div style={{ padding: 10, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Pending review (deploy gate)</div>
              <div className="mono" style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, marginTop: 4, color: data.pending_source === 'unavailable' ? 'var(--text-dim)' : data.pending_count > 0 ? 'var(--warn)' : 'var(--text)' }}>{data.pending_source === 'unavailable' ? '—' : data.pending_count}</div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                {data.pending_source === 'unavailable' ? 'review queue unavailable' : data.pending_count === 0 ? 'all clear — nothing awaiting review' : data.pending_count === 1 ? '1 PR awaiting review' : `${data.pending_count} PRs awaiting review`}</div>
            </div>

            <div style={{ padding: 10, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Last session</div>
              {last ? (
                <>
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <StatusDot tone={last.status === 'completed' ? 'ok' : last.status === 'running' ? 'info' : 'warn'} />
                    <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{last.reviewed_total} reviewed</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {last.passed > 0 && <span style={{ color: 'var(--ok)' }}>✓ {last.passed} passed</span>}
                    {last.partial > 0 && <span style={{ color: 'var(--warn)' }}>~ {last.partial} partial</span>}
                    {last.phantom > 0 && <span style={{ color: 'var(--err)' }}>✗ {last.phantom} phantom</span>}
                    {last.skipped > 0 && <span style={{ color: 'var(--text-dim)' }}>– {last.skipped} skipped</span>}
                    {last.reviewed_total === 0 && <span>no items</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                    {formatTime(new Date(last.started_at))} · {lastDuration}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 6 }}>no sessions yet</div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Recent sessions</div>
            {data.recent_sessions.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>no sessions</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ color: 'var(--text-dim)' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Started</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Reviewed</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Verdicts</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_sessions.map(s => (
                    <tr key={s.session_id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td className="mono" style={{ padding: '4px 6px' }}>
                        {formatDateWithOptions(s.started_at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <Pill variant={s.status === 'completed' ? 'ok' : s.status === 'running' ? 'live' : 'warn'}>{s.status}</Pill>
                      </td>
                      <td className="mono" style={{ textAlign: 'right', padding: '4px 6px' }}>{s.reviewed_total}</td>
                      <td style={{ padding: '4px 6px', fontSize: 10, color: 'var(--text-dim)' }}>
                        {s.passed > 0 && <span style={{ color: 'var(--ok)', marginRight: 6 }}>✓{s.passed}</span>}
                        {s.partial > 0 && <span style={{ color: 'var(--warn)', marginRight: 6 }}>~{s.partial}</span>}
                        {s.phantom > 0 && <span style={{ color: 'var(--err)', marginRight: 6 }}>✗{s.phantom}</span>}
                        {s.skipped > 0 && <span style={{ color: 'var(--text-dim)' }}>–{s.skipped}</span>}
                      </td>
                      <td className="mono" style={{ textAlign: 'right', padding: '4px 6px', color: 'var(--text-dim)' }}>
                        {s.duration_seconds != null
                          ? (s.duration_seconds < 60 ? `${s.duration_seconds}s` : `${Math.round(s.duration_seconds / 60)}m`)
                          : (s.status === 'running' ? 'live' : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
              Recent verdicts ({data.recent_verdicts.length})
            </div>
            {data.recent_verdicts.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>no verdicts</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto' }}>
                {data.recent_verdicts.map((v, i) => (
                  <div key={`${v.row_id}-${i}`} style={{
                    display: 'grid', gridTemplateColumns: '90px 160px 1fr 80px',
                    gap: 8, fontSize: 11, alignItems: 'baseline',
                    padding: '4px 0', borderBottom: '1px solid var(--border)',
                  }}>
                    <span className="mono" style={{
                      color: v.verdict === 'passed' ? 'var(--ok)'
                        : v.verdict === 'partial' ? 'var(--warn)'
                        : v.verdict === 'phantom' ? 'var(--err)' : 'var(--text-dim)',
                      fontWeight: 600,
                    }}>{v.verdict}</span>
                    <Pill variant="ghost">{v.row_id.length > 22 ? v.row_id.slice(0, 21) + '…' : v.row_id}</Pill>
                    <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.evidence}</span>
                    <span className="mono" style={{ textAlign: 'right', color: 'var(--text-dim)', fontSize: 10 }}>
                      {formatTime(new Date(v.reviewed_at))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </CollapsiblePanel>
  )
}

function AgentRunsCard() {
  const [paused, setPaused] = useState<boolean | null>(null)
  const [runs, setRuns] = useState<HubAgentRun[] | null>(null)
  const [toggling, setToggling] = useState(false)

  const load = useCallback(async () => {
    const [statusRes, runsRes] = await Promise.all([
      hubAgentRuns.status().catch(() => null),
      hubAgentRuns.list(50).catch(() => null),
    ])
    if (statusRes) setPaused(statusRes.paused)
    if (runsRes) setRuns(runsRes.runs)
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [load])

  const toggle = async () => {
    if (paused === null || toggling) return
    setToggling(true)
    try {
      const res = paused
        ? await hubAgentRuns.resume().catch(() => null)
        : await hubAgentRuns.pause().catch(() => null)
      if (res != null) setPaused(res.paused)
    } finally {
      setToggling(false)
      load()
    }
  }

  const killSwitchRight = (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      <Pill variant={paused ? 'warn' : 'ok'}>{paused ? 'paused' : 'running'}</Pill>
      <button
        type="button"
        className={`btn ${paused ? 'primary' : 'ghost'}`}
        style={{ height: 24, fontSize: 11, padding: '0 10px' }}
        onClick={toggle}
        disabled={toggling || paused === null}
      >
        {toggling ? '…' : paused ? 'Resume' : 'Pause'}
      </button>
    </div>
  )

  return (
    <Card
      title="Marketing agent dispatcher"
      endpoint="/hub/agent-runs"
      bodyPad={false}
      right={killSwitchRight}
    >
      {runs === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {runs !== null && runs.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>
          No agent runs yet.
        </div>
      )}
      {runs !== null && runs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['task_type', 'trigger', 'status', 'model', 'cost', 'started', 'duration'].map(col => (
                <th
                  key={col}
                  style={{
                    padding: '6px 10px', textAlign: 'left', fontSize: 10.5,
                    color: 'var(--text-dim)', fontWeight: 500,
                  }}
                >
                  {col.replace('_', ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((r, i) => (
              <tr key={r.id} style={{ borderTop: i ? '1px solid var(--border)' : 0 }}>
                <td style={{ padding: '7px 10px' }} className="mono">{r.task_type}</td>
                <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{r.trigger}</td>
                <td style={{ padding: '7px 10px' }}>
                  <Pill variant={agentRunStatusTone(r.status)}>{r.status}</Pill>
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }} className="mono">
                  {r.model || '—'}
                </td>
                <td style={{ padding: '7px 10px' }} className="mono">
                  {r.cost_usd > 0 ? `$${r.cost_usd.toFixed(4)}` : '—'}
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--text-dim)' }} className="mono">
                  {r.started_at ? relTime(r.started_at) : '—'}
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--text-dim)' }} className="mono">
                  {r.started_at && r.finished_at
                    ? fmtRunDuration(r.started_at, r.finished_at)
                    : r.error
                      ? <span style={{ color: 'var(--err)' }} title={r.error}>err</span>
                      : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function agentRunStatusTone(s: string): 'ok' | 'warn' | 'err' | 'live' | 'ghost' {
  if (s === 'done') return 'ok'
  if (s === 'running') return 'live'
  if (s === 'failed') return 'err'
  if (s === 'pending') return 'warn'
  return 'ghost'
}

function fmtRunDuration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (!isFinite(ms) || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  return `${m}m${s % 60}s`
}

// ── helpers ──────────────────────────────────────────────────────────
function bucketHourly(items: { created_at: string }[]): number[] {
  const buckets = new Array(24).fill(0)
  const now = Date.now()
  for (const e of items) {
    const ageMs = now - new Date(e.created_at).getTime()
    const ageHr = Math.floor(ageMs / (3600 * 1000))
    if (ageHr < 0 || ageHr >= 24) continue
    buckets[23 - ageHr]++
  }
  return buckets
}

function buildShipsSeries(items: GithubIssueItem[]): number[] {
  const buckets = new Array(24).fill(0)
  const now = Date.now()
  for (const it of items) {
    if (!it.shipped_at) continue
    const ageMs = now - new Date(it.shipped_at).getTime()
    const ageHr = Math.floor(ageMs / (3600 * 1000))
    if (ageHr < 0 || ageHr >= 24) continue
    buckets[23 - ageHr]++
  }
  return buckets
}

function prioToneClass(p: string | null | undefined): string {
  if (p === 'p0') return 'err'
  if (p === 'p1') return 'warn'
  if (p === 'p2') return 'info'
  return 'ghost'
}

function statusToneClass(s: string): string {
  if (s === 'preparing') return 'warn'
  if (s === 'in_progress') return 'live'
  if (s === 'blocked') return 'err'
  if (s === 'done') return 'ok'
  if (s === 'deployed') return 'ok'
  return 'ghost'
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0 || !isFinite(ms)) return '—'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  return `${day}d`
}

function formatUptime(secs: number): string {
  if (!secs || secs < 0) return '—'
  const day = Math.floor(secs / 86400)
  const hr = Math.floor((secs % 86400) / 3600)
  const min = Math.floor((secs % 3600) / 60)
  if (day > 0) return `${day}d ${hr}h`
  if (hr > 0) return `${hr}h ${min}m`
  if (min > 0) return `${min}m`
  return `${secs}s`
}
